import { Plus, RefreshCw } from "lucide-react";
import { btnPrimaryClass, btnSecondarySmClass, inputBaseClass } from "./constants";
import { ApiResponsePre, SectionCard } from "./shared";

const DEST_KINDS = [
  {
    id: "webhook",
    label: "Webhook URL",
    hint: "POST the event as-is to a URL you own."
  },
  {
    id: "endpoint",
    label: "Connected API",
    hint: "Map the event onto an endpoint from your integrations, using its saved credentials."
  }
];

function DestinationCell({ row }) {
  if (row.provider_api_endpoint_id) {
    return (
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold bg-indigo-50 text-indigo-700 shrink-0">
            {row.endpoint_method || "—"}
          </span>
          <span className="font-semibold truncate">
            {row.endpoint_name || "Connected API"}
          </span>
        </div>
        <div
          className="font-mono text-[11px] text-gray-500 truncate"
          title={row.endpoint_path || ""}
        >
          {row.endpoint_path || ""}
        </div>
      </div>
    );
  }

  return (
    <div className="min-w-0">
      <div className="font-semibold truncate">{row.webhook_name || "—"}</div>
      <div
        className="font-mono text-[11px] text-gray-500 truncate"
        title={row.webhook_url || ""}
      >
        {row.webhook_url || ""}
      </div>
    </div>
  );
}

export function NotificationSubscriptionsTab({
  lexaiEvents,
  nsEventId,
  setNsEventId,
  nsWebhookId,
  setNsWebhookId,
  nsDestKind,
  setNsDestKind,
  nsEndpointId,
  setNsEndpointId,
  endpointCatalog = [],
  nsPayloadTemplate,
  setNsPayloadTemplate,
  nsDedupKeyTemplate,
  setNsDedupKeyTemplate,
  nsDedupWindow,
  setNsDedupWindow,
  nsResponse,
  nsTestResponse,
  notificationRows,
  activeWebhooks,
  loadNotificationSettingsList,
  onCreateNS,
  onToggleNS,
  onDeleteNS,
  onTestNS
}) {
  const isEndpoint = nsDestKind === "endpoint";

  // Group the picker by API so a long flat list of endpoint names stays readable.
  const groupedEndpoints = endpointCatalog.reduce((acc, ep) => {
    const key = ep.api_name || "Other";
    (acc[key] = acc[key] || []).push(ep);
    return acc;
  }, {});

  return (
    <>
      <div className="mb-5">
        <SectionCard
          title="Subscribe to Event"
          subtitle={
            <>
              Send a LexAI event to a destination of your choice. Use{" "}
              <code className="bg-gray-100 px-1 rounded">*</code> to catch all events.
            </>
          }
        >
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex flex-col gap-1 min-w-[180px] flex-1">
                <label className="text-xs font-medium text-gray-600">Event Type</label>
                <select
                  className={inputBaseClass}
                  value={nsEventId}
                  onChange={(e) => setNsEventId(e.target.value)}
                >
                  {!lexaiEvents.length ? (
                    <option value="">No events loaded — Refresh LexAI tab</option>
                  ) : (
                    lexaiEvents.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.event_name === "*" && e.service_name === "*"
                          ? "* — All events (wildcard)"
                          : `${e.service_name} — ${e.event_name}`}
                      </option>
                    ))
                  )}
                </select>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-gray-600">Send it to</label>
              <div className="flex flex-wrap gap-2">
                {DEST_KINDS.map((k) => {
                  const selected = nsDestKind === k.id;
                  const disabled = k.id === "endpoint" && endpointCatalog.length === 0;
                  return (
                    <button
                      key={k.id}
                      type="button"
                      disabled={disabled}
                      onClick={() => setNsDestKind(k.id)}
                      title={
                        disabled
                          ? "No connected APIs yet — add an integration first"
                          : k.hint
                      }
                      className={`text-left px-3 py-2 rounded-lg border text-xs transition min-w-[210px] ${
                        selected
                          ? "border-blue-500 bg-blue-50 text-blue-900"
                          : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      <div className="font-semibold">{k.label}</div>
                      <div className="text-[11px] text-gray-500 mt-0.5">{k.hint}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {!isEndpoint ? (
              <div className="flex flex-wrap gap-3 items-end">
                <div className="flex flex-col gap-1 min-w-[180px] flex-1">
                  <label className="text-xs font-medium text-gray-600">Webhook</label>
                  <select
                    className={inputBaseClass}
                    value={nsWebhookId}
                    onChange={(e) => setNsWebhookId(e.target.value)}
                  >
                    <option value="">— select webhook —</option>
                    {activeWebhooks.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-gray-600">Endpoint</label>
                  <select
                    className={inputBaseClass}
                    value={nsEndpointId}
                    onChange={(e) => setNsEndpointId(e.target.value)}
                  >
                    <option value="">— select endpoint —</option>
                    {Object.entries(groupedEndpoints).map(([apiName, eps]) => (
                      <optgroup key={apiName} label={apiName}>
                        {eps.map((ep) => (
                          <option key={ep.endpoint_id} value={ep.endpoint_id}>
                            {ep.http_method} {ep.path} — {ep.endpoint_name}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  <p className="text-[11px] text-gray-500">
                    Uses this integration&apos;s saved credentials, base URL and retry
                    settings. Every call is recorded in your API logs.
                  </p>
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-gray-600">
                    Payload mapping <span className="text-gray-400">(optional)</span>
                  </label>
                  <textarea
                    className={`${inputBaseClass} font-mono text-xs`}
                    rows={3}
                    placeholder={'{"body": "{{ data }}"}'}
                    value={nsPayloadTemplate}
                    onChange={(e) => setNsPayloadTemplate(e.target.value)}
                  />
                  <p className="text-[11px] text-gray-500">
                    Leave blank to send the event&apos;s{" "}
                    <code className="bg-gray-100 px-1 rounded">data</code> object as the
                    request body. Values are templates, so{" "}
                    <code className="bg-gray-100 px-1 rounded">
                      {'{"body": "{{ data }}"}'}
                    </code>{" "}
                    forwards it unchanged.
                  </p>
                </div>

                <div className="flex flex-wrap gap-3 items-start">
                  <div className="flex flex-col gap-1 min-w-[240px] flex-1">
                    <label className="text-xs font-medium text-gray-600">
                      Ignore duplicates by{" "}
                      <span className="text-gray-400">(optional)</span>
                    </label>
                    <input
                      className={`${inputBaseClass} font-mono text-xs`}
                      placeholder="{{ data.submissionReference }}"
                      value={nsDedupKeyTemplate}
                      onChange={(e) => setNsDedupKeyTemplate(e.target.value)}
                    />
                    <p className="text-[11px] text-gray-500">
                      If two events share this value, only the first is sent. Protects
                      the receiving system from a sender that fires repeatedly.
                    </p>
                  </div>
                  <div className="flex flex-col gap-1 w-[150px]">
                    <label className="text-xs font-medium text-gray-600">
                      Within (seconds)
                    </label>
                    <input
                      className={inputBaseClass}
                      type="number"
                      min="1"
                      value={nsDedupWindow}
                      onChange={(e) => setNsDedupWindow(e.target.value)}
                      disabled={!nsDedupKeyTemplate.trim()}
                    />
                    <p className="text-[11px] text-gray-500">
                      After this, the same value sends again.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div>
              <button type="button" onClick={onCreateNS} className={btnPrimaryClass}>
                <Plus className="w-4 h-4" />
                Subscribe
              </button>
            </div>
          </div>
          <ApiResponsePre ok={nsResponse?.ok} text={nsResponse?.text} />
        </SectionCard>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-[15px] font-semibold text-gray-900">
              Active Subscriptions
            </h2>
            <p className="text-xs text-gray-500">
              Event → destination mappings for your org
            </p>
          </div>
          <button
            type="button"
            onClick={loadNotificationSettingsList}
            className={btnSecondarySmClass}
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left py-2.5 px-3 text-[11px] font-semibold uppercase text-gray-500">
                  Service
                </th>
                <th className="text-left py-2.5 px-3 text-[11px] font-semibold uppercase text-gray-500">
                  Event
                </th>
                <th className="text-left py-2.5 px-3 text-[11px] font-semibold uppercase text-gray-500">
                  Type
                </th>
                <th className="text-left py-2.5 px-3 text-[11px] font-semibold uppercase text-gray-500">
                  Destination
                </th>
                <th className="text-left py-2.5 px-3 text-[11px] font-semibold uppercase text-gray-500">
                  Duplicates
                </th>
                <th className="text-left py-2.5 px-3 text-[11px] font-semibold uppercase text-gray-500">
                  Enabled
                </th>
                <th className="text-left py-2.5 px-3 text-[11px] font-semibold uppercase text-gray-500">
                  Test
                </th>
                <th className="text-left py-2.5 px-3 text-[11px] font-semibold uppercase text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {!notificationRows.length ? (
                <tr>
                  <td colSpan={8} className="py-10 text-center text-gray-400">
                    No subscriptions yet.
                  </td>
                </tr>
              ) : (
                notificationRows.map((s) => {
                  const isWildcard = s.event_name === "*" && s.service_name === "*";
                  const viaEndpoint = !!s.provider_api_endpoint_id;
                  return (
                    <tr key={s.id} className="border-b border-gray-100 hover:bg-slate-50/80">
                      <td className="py-3 px-3">
                        {isWildcard ? (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold bg-violet-50 text-violet-700">
                            * All
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-700">
                            {s.service_name}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-3">
                        {isWildcard ? (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold bg-violet-50 text-violet-700">
                            * All events
                          </span>
                        ) : (
                          <code className="bg-gray-100 px-2 py-0.5 rounded text-xs">
                            {s.event_name}
                          </code>
                        )}
                      </td>
                      <td className="py-3 px-3">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                            viaEndpoint
                              ? "bg-indigo-50 text-indigo-700"
                              : "bg-sky-50 text-sky-700"
                          }`}
                        >
                          {viaEndpoint ? "Connected API" : "Webhook"}
                        </span>
                      </td>
                      <td className="py-3 px-3 max-w-[260px]">
                        <DestinationCell row={s} />
                      </td>
                      <td className="py-3 px-3">
                        {s.dedup_key_template ? (
                          <span
                            className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-800"
                            title={`Ignoring repeats of ${s.dedup_key_template} within ${s.dedup_window_seconds}s`}
                          >
                            Ignored · {s.dedup_window_seconds}s
                          </span>
                        ) : (
                          <span className="text-[11px] text-gray-400">All sent</span>
                        )}
                      </td>
                      <td className="py-3 px-3">
                        <label className="inline-flex items-center gap-2 cursor-pointer text-xs text-gray-600">
                          <input
                            type="checkbox"
                            checked={!!s.is_enabled}
                            onChange={(e) => onToggleNS(s.id, e.target.checked)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          {s.is_enabled ? "On" : "Off"}
                        </label>
                      </td>
                      <td className="py-3 px-3">
                        <button
                          type="button"
                          onClick={() => onTestNS(s.lexai_event_id)}
                          title={
                            viaEndpoint
                              ? "Sends a REAL request to this endpoint with your saved credentials"
                              : "Fires a sample payload at this webhook"
                          }
                          className={`text-xs px-2 py-1 rounded-md border ${
                            viaEndpoint
                              ? "border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"
                              : "border-green-200 bg-green-50 text-green-800 hover:bg-green-100"
                          }`}
                        >
                          {viaEndpoint ? "▶ Send live" : "▶ Test"}
                        </button>
                      </td>
                      <td className="py-3 px-3">
                        <button
                          type="button"
                          onClick={() => onDeleteNS(s.id)}
                          className="text-xs px-2 py-1 rounded-md border border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <ApiResponsePre ok={nsTestResponse?.ok} text={nsTestResponse?.text} />
      </div>
    </>
  );
}
